// src/components/padrekko/qc/builder/CaseBuilderPanel.jsx
//
// Left panel for the case builder: source-layer picker, buffer size, and the
// pad table (single source of truth = useCaseBuilderStore.pads). Draft pads
// ONLY — case name / participants / autodetect are collected later in
// NewCaseForm. Continue/Cancel live in the banner (useCaseBuilderActions). All
// pad state_codes are read-only/derived — the authoritative value comes from
// the classify-pads endpoint (county lookup), so an editable dropdown would
// only invite a choice the server discards.

import { useEffect, useMemo, useRef } from "react";
import { Trash2, Pencil, Check, MapPin, AlertTriangle, Loader2, Crosshair } from "lucide-react";
import { useCaseBuilderStore } from "../../../../stores/useCaseBuilderStore";
import { useUserLayersStore } from "../../../../stores/useUserLayersStore";
import { fetchWithAuth } from "../../../../api/fetchWithAuth";
import { polygonBbox, extractPoints } from "../../../../lib/padBuffer";

// Display-only short framework labels (the server stores the authoritative
// long-form string; this just mirrors state.js for the table).
const FRAMEWORK_SHORT = { TX: "RRC 16 TAC / TCEQ + UL", NM: "NM OCD (NMAC 19.15) + UL" };

function StateCell({ pad }) {
  if (pad.region === "pending" || pad.region === undefined) {
    return <span className="inline-flex items-center gap-1 text-[11px] text-fg-secondary"><Loader2 size={11} className="animate-spin" /> …</span>;
  }
  if (pad.region !== "ok") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-red-300" title={pad.region}>
        <AlertTriangle size={11} /> {pad.region === "unsupported_state" ? (pad.state_code ?? "?") : "Outside TX/NM"}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-300">
      {pad.state_code}
    </span>
  );
}

export default function CaseBuilderPanel() {
  const pads = useCaseBuilderStore((s) => s.pads);
  const sourceLayerId = useCaseBuilderStore((s) => s.sourceLayerId);
  const bufferMeters = useCaseBuilderStore((s) => s.bufferMeters);
  const selectedPadId = useCaseBuilderStore((s) => s.selectedPadId);
  const focusedPadId = useCaseBuilderStore((s) => s.focusedPadId);

  const setSourceLayer = useCaseBuilderStore((s) => s.setSourceLayer);
  const focusPad = useCaseBuilderStore((s) => s.focusPad);
  const setBufferMeters = useCaseBuilderStore((s) => s.setBufferMeters);
  const updatePad = useCaseBuilderStore((s) => s.updatePad);
  const removePad = useCaseBuilderStore((s) => s.removePad);
  const setSelectedPad = useCaseBuilderStore((s) => s.setSelectedPad);
  const setHighlightPad = useCaseBuilderStore((s) => s.setHighlightPad);
  const applyClassification = useCaseBuilderStore((s) => s.applyClassification);
  const discardDraft = useCaseBuilderStore((s) => s.discardDraft);

  const ownLayers = useUserLayersStore((s) => s.layers);
  const sharedLayers = useUserLayersStore((s) => s.shared);

  // Point layers available as a selection source (own + shared, deduped). A
  // layer is point-eligible if any feature yields points via extractPoints
  // (Point OR MultiPoint); a mixed layer qualifies but only its point features
  // are used downstream. Lines/polygons are never offered.
  const pointLayers = useMemo(() => {
    const all = [...(ownLayers || []), ...(sharedLayers || [])];
    const seen = new Set();
    return all.filter((l) => {
      if (!l || seen.has(l.id)) return false;
      seen.add(l.id);
      return (l.geojson?.features || []).some((f) => extractPoints(f).length > 0);
    });
  }, [ownLayers, sharedLayers]);

  // Default the source layer to the sole eligible point layer. The eligible
  // list is async-populated (a freshly imported My Layers layer's geojson loads
  // after upload), so re-evaluate whenever it changes; auto-select only when
  // exactly one layer qualifies, and never override a manual/persisted pick.
  useEffect(() => {
    if (sourceLayerId) return;
    if (pointLayers.length === 1) setSourceLayer(pointLayers[0].id);
  }, [sourceLayerId, pointLayers, setSourceLayer]);

  // ── classify pads (authoritative state + in-region verdict) ──
  // Re-runs only when the pad set or any geometry changes (sig guard), never on
  // its own result write — applyClassification doesn't touch geometry.
  const sigRef = useRef("");
  useEffect(() => {
    const sig = pads.map((p) => p.id + JSON.stringify(p.geometry)).join("|");
    if (sig === sigRef.current || !pads.length) return;
    sigRef.current = sig;
    let live = true;
    fetchWithAuth("/api/v1/padrekko/qc/classify-pads", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pads: pads.map((p) => ({ geometry: p.geometry })) }),
    })
      .then((r) => (r.ok ? r.json() : { pads: [] }))
      .then((d) => {
        if (!live) return;
        const results = d.pads || [];
        const byId = {};
        pads.forEach((p, i) => { if (results[i]) byId[p.id] = results[i]; });
        applyClassification(byId);
      })
      .catch((e) => { console.error("[ui/src/components/padrekko/qc/builder/CaseBuilderPanel.jsx] POST /api/v1/padrekko/qc/classify-pads failed (swallowed):", e); });
    return () => { live = false; };
  }, [pads, applyClassification]);

  return (
    <div className="h-full flex flex-col text-fg-primary">
      <div className="shrink-0 px-4 py-2 border-b border-slate-800">
        <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary">PadRekko</div>
        <h2 className="text-[14px] font-semibold flex items-center gap-1.5">
          <MapPin size={14} className="text-accent-info" /> New case from selection
        </h2>
      </div>

      <div className="flex-1 min-h-0 flex flex-col px-4 pt-2 pb-3">
        {/* Fixed controls — source, pad size, and the case form stay pinned;
            only the pad list below scrolls. */}
        <div className="shrink-0 space-y-3">
        {/* Source layer + pad size on one row (85/15) — label above, picker below */}
        <div>
          <div className="grid grid-cols-[80fr_20fr] gap-2 items-start">
            <div className="min-w-0">
              <label className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary">Source point layer</label>
              <select
                value={sourceLayerId || ""}
                onChange={(e) => setSourceLayer(e.target.value)}
                className="mt-1 w-full text-[12px] bg-slate-800/40 border border-slate-700/60 rounded px-2 py-1.5 text-slate-100 [color-scheme:dark] focus:outline-none focus:border-accent-info/50"
              >
                {!pointLayers.length && <option value="" className="bg-slate-800 text-slate-100">No point layers — import one first</option>}
                {/* Explicit empty option so an unselected picker (multiple eligible
                    layers → no auto-select) shows a "choose me" cue instead of
                    rendering the first layer's name as if it were selected. */}
                {pointLayers.length > 0 && <option value="" className="bg-slate-800 text-slate-100">Select a point layer …</option>}
                {pointLayers.map((l) => (
                  <option key={l.id} value={l.id} className="bg-slate-800 text-slate-100">{l.name}</option>
                ))}
              </select>
            </div>
            <div className="min-w-0">
              <label className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary">Size (m)</label>
              <input
                type="number"
                min={10}
                step={10}
                value={bufferMeters}
                onChange={(e) => setBufferMeters(e.target.value)}
                className="mt-1 w-full text-[12px] bg-slate-800/40 border border-slate-700/60 rounded px-1.5 py-1.5 text-slate-100 [color-scheme:dark] focus:outline-none focus:border-accent-info/50"
              />
            </div>
          </div>
          <div className="text-[10px] text-fg-secondary mt-1">Applies to the next shift-drag selection.</div>
        </div>
        </div>

        {/* Pad list — fills the remaining height and scrolls on its own */}
        <div className="flex-1 min-h-0 flex flex-col mt-3">
          <div className="flex items-center justify-between mb-1 shrink-0">
            <label className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary">
              Pads ({pads.length})
            </label>
            {pads.length > 0 && (
              <button onClick={discardDraft} className="text-[10px] text-fg-secondary hover:text-red-300 transition">
                Clear all
              </button>
            )}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
          {pads.length === 0 ? (
            <div className="text-[11px] text-fg-secondary border border-dashed border-slate-700 rounded px-3 py-4 text-center">
              Shift-drag a box on the map over points in the source layer to create pads.
            </div>
          ) : (
            <div className="space-y-1">
              {pads.map((p, i) => {
                const bb = polygonBbox(p.geometry);
                const editing = p.id === selectedPadId;
                const focused = p.id === focusedPadId;
                return (
                  <div
                    key={p.id}
                    onMouseEnter={() => setHighlightPad(p.id)}
                    onMouseLeave={() => setHighlightPad(null)}
                    className={`rounded border px-2 py-1.5 ${editing || focused ? "border-accent-action" : "border-slate-700/60 bg-slate-800/30"}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] tabular-nums text-fg-secondary w-4">{i + 1}</span>
                      <input
                        value={p.pad_name}
                        onChange={(e) => updatePad(p.id, { pad_name: e.target.value })}
                        placeholder="Pad name"
                        className="flex-1 min-w-0 text-[12px] bg-transparent border-b border-transparent hover:border-slate-700 focus:border-accent-action/60 focus:outline-none text-slate-100 placeholder:text-slate-600"
                      />
                      <StateCell pad={p} />
                      <button
                        onClick={() => setSelectedPad(editing ? null : p.id)}
                        title={editing ? "Finish editing" : "Edit vertices"}
                        className={`p-1 rounded transition ${editing ? "text-accent-action" : "text-fg-secondary hover:text-slate-200"}`}
                      >
                        {editing ? <Check size={13} /> : <Pencil size={13} />}
                      </button>
                      <button
                        onClick={() => focusPad(p.id)}
                        title="Zoom to pad"
                        className={`p-1 rounded transition ${focused ? "text-accent-action" : "text-fg-secondary hover:text-accent-action"}`}
                      >
                        <Crosshair size={13} />
                      </button>
                      <button
                        onClick={() => removePad(p.id)}
                        title="Remove pad"
                        className="p-1 rounded text-fg-secondary hover:text-red-300 transition"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                    <div className="flex items-center gap-2 pl-6 mt-0.5 text-[10px] text-fg-secondary">
                      {p.region === "ok" && p.state_code && (
                        <span>{FRAMEWORK_SHORT[p.state_code]}</span>
                      )}
                      {bb && (
                        <span className="ml-auto tabular-nums">
                          {bb.south.toFixed(3)},{bb.west.toFixed(3)} → {bb.north.toFixed(3)},{bb.east.toFixed(3)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
